import { useMemo, forwardRef, useRef } from 'react';
import { Editor } from '@tiptap/core';

import * as Styled from './richtext-inline-toolbar.style';
import {
  FONT_CALIBRE_SCHOOL,
  FONT_NSW_HANDWRITING,
} from 'approot/shared/styles/typography.style';
import {
  isSelectionBold,
  isSelectionBulletList,
  isSelectionItalic,
  isSelectionOrderedList,
  isSelectionUnderlined,
  toggleBold,
  toggleBulletList,
  toggleItalic,
  toggleOrderedList,
  toggleUnderline,
  getFontFamily,
} from './richtext.utils';
import mergeRefs from 'lib/react/merge-refs';
import { useToolbarTrap } from './richtext.hooks';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { isMacPlatform } from 'approot/shared/ally/is-mac-platform';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';

export type Props = {
  editor: Editor;
};

const RichtextInlineToolbar = forwardRef<HTMLDivElement | null, Props>(
  ({ editor }: Props, ref) => {
    const toolbarRef = useRef<HTMLDivElement | null>(null);

    useToolbarTrap(toolbarRef, () => editor.chain().focus());

    return (
      <Styled.RichtextInlineToolbar
        ref={mergeRefs<HTMLDivElement | null>([ref, toolbarRef])}
      >
        <RichtextToolbarButtons editor={editor} />
      </Styled.RichtextInlineToolbar>
    );
  }
);

export default RichtextInlineToolbar;

const RichtextToolbarButtons = ({ editor }: Props) => {
  const currentFontFamily = getFontFamily(editor) || FONT_CALIBRE_SCHOOL;
  const isBold = isSelectionBold(editor);
  const isItalic = isSelectionItalic(editor);
  const isUnderlined = isSelectionUnderlined(editor);
  const isBulletList = isSelectionBulletList(editor);
  const isOrderedList = isSelectionOrderedList(editor);

  const metaKey = useMemo(() => (isMacPlatform() ? '&#8984;' : 'ctrl'), []);

  const includesBold = useMemo(
    () =>
      editor.extensionManager.extensions.some(
        extension => extension.name === 'bold'
      ),
    [editor.extensionManager.extensions]
  );

  const includesItalic = useMemo(
    () =>
      editor.extensionManager.extensions.some(
        extension => extension.name === 'italic'
      ),
    [editor.extensionManager.extensions]
  );

  const includesUnderline = useMemo(
    () =>
      editor.extensionManager.extensions.some(
        extension => extension.name === 'underline'
      ),
    [editor.extensionManager.extensions]
  );

  const includesBulletList = useMemo(
    () =>
      editor.extensionManager.extensions.some(
        extension => extension.name === 'bulletList'
      ),
    [editor.extensionManager.extensions]
  );

  const includesOrderedList = useMemo(
    () =>
      editor.extensionManager.extensions.some(
        extension => extension.name === 'orderedList'
      ),
    [editor.extensionManager.extensions]
  );

  return (
    <>
      {includesBold && (
        <TooltipWrapper hoverText={`Bold (${metaKey}+b)`}>
          <Styled.RichtextIconButton
            data-selector="icon-bold"
            $selected={isBold}
            onClick={() => toggleBold(editor)}
            disabled={currentFontFamily === FONT_NSW_HANDWRITING}
            aria-label={`${
              isBold
                ? 'Remove bold from the selected text'
                : 'Make the selected text bold'
            }`}
          >
            <ActivityBuilderInlineSvg id="icon_bold" />
          </Styled.RichtextIconButton>
        </TooltipWrapper>
      )}

      {includesItalic && (
        <TooltipWrapper hoverText={`Italic (${metaKey}+i)`}>
          <Styled.RichtextIconButton
            data-selector="icon-italic"
            $selected={isItalic}
            onClick={() => toggleItalic(editor)}
            disabled={currentFontFamily === FONT_NSW_HANDWRITING}
            aria-label={`${
              isItalic
                ? 'Remove italic from the selected text'
                : 'Make the selected text italic'
            }`}
          >
            <ActivityBuilderInlineSvg id="icon_italic" />
          </Styled.RichtextIconButton>
        </TooltipWrapper>
      )}

      {includesUnderline && (
        <TooltipWrapper hoverText={`Underline (${metaKey}+u)`}>
          <Styled.RichtextIconButton
            data-selector="icon-underline"
            $selected={isUnderlined}
            onClick={() => toggleUnderline(editor)}
            aria-label={`${
              isUnderlined
                ? 'Remove underline from the selected text'
                : 'Make the selected text underlined'
            }`}
          >
            <ActivityBuilderInlineSvg id="icon_underline" />
          </Styled.RichtextIconButton>
        </TooltipWrapper>
      )}

      {includesBulletList && (
        <TooltipWrapper hoverText={`Bullets (${metaKey}+shift+8)`}>
          <Styled.RichtextIconButton
            data-selector="icon-bullet"
            $selected={isBulletList}
            onClick={() => toggleBulletList(editor)}
            aria-label={`${
              isBulletList
                ? 'Remove bullet list from the selected text'
                : 'Make the selected text a bullet list'
            }`}
          >
            <ActivityBuilderInlineSvg id="icon_bullet_list" />
          </Styled.RichtextIconButton>
        </TooltipWrapper>
      )}

      {includesOrderedList && (
        <TooltipWrapper hoverText={`Numbers (${metaKey}+shift+7)`}>
          <Styled.RichtextIconButton
            data-selector="icon-numbering"
            $selected={isOrderedList}
            onClick={() => toggleOrderedList(editor)}
            aria-label={`${
              isOrderedList
                ? 'Remove numbered list from the selected text'
                : 'Make the selected text a numbered list'
            }`}
          >
            <ActivityBuilderInlineSvg id="icon_number_list" />
          </Styled.RichtextIconButton>
        </TooltipWrapper>
      )}
    </>
  );
};
